import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import { Form, FormField, useForm } from "@ui/components/Form";
import { InputIcon, TextInput } from "@ui/components/Inputs";
import { SearchFormSubmitButton } from "@ui/components/Search/components/SearchForm/components/SearchFormSubmitButton/SearchFormSubmitButton";
import { useTranslations } from "@ui/providers/useTranslations";
import { navigateTo } from "@ui/utils/navigateTo";

import { UiCrossIcon } from "@jti/brand-icons/ui-cross";

import type { TranslationsBox } from "../../types";

import styles from "./SearchForm.module.scss";

export type SearchFormProps = {
  translations?: TranslationsBox;
  resultsPage?: string;
  initialSearchValue?: string;
  isInputAutoFocus?: boolean;
  minSearchTextLength?: number;
  onSearchSubmit?: (searchText: string) => void;
  navigationFn?: (url: string) => void;
};

export const SearchForm: FC<SearchFormProps> = ({
  translations,
  resultsPage,
  initialSearchValue,
  isInputAutoFocus = true,
  minSearchTextLength = 3,
  onSearchSubmit,
  navigationFn,
}) => {
  const { __ } = useTranslations();
  const form = useForm({ defaultValues: { searchValue: "" } });
  const [isPendingLocationChange, setPendingLocationChange] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const {
    reset,
    watch,
    formState: { isDirty, isValid },
  } = form;

  const searchValue = watch("searchValue");

  const handleSubmit = () => {
    if (resultsPage && window.location.pathname !== resultsPage) {
      setPendingLocationChange(true);
      return navigateTo({ pathName: resultsPage, params: { q: searchValue }, navigationFn });
    }

    onSearchSubmit?.(searchValue);
  };

  const clearForm = () => {
    reset({
      searchValue: "",
    });
  };

  useEffect(() => {
    if (initialSearchValue) {
      form.setValue("searchValue", initialSearchValue, { shouldDirty: true, shouldValidate: true });
    }
  }, [form, initialSearchValue]);

  // Focus imperatively with preventScroll instead of native autoFocus: native autoFocus makes
  // the browser scroll the whole page to bring the input into view, which resets the page's
  // scroll position when the search form opens while the page is scrolled down.
  useEffect(() => {
    if (isInputAutoFocus) {
      searchInputRef.current?.focus({ preventScroll: true });
    }
  }, [isInputAutoFocus]);

  return (
    <div className={styles.searchFormContainer} data-testid="searchFormContainer">
      <Form form={form} onSubmit={handleSubmit} dataTestId="searchForm">
        <div className={styles.searchFormWrapper}>
          <div className={styles.searchInputWrapper}>
            <FormField
              name="searchValue"
              className={styles.searchInputFormField}
              render={({ field }) => (
                <TextInput
                  {...field}
                  elemAfter={
                    searchValue ? (
                      <InputIcon
                        icon={UiCrossIcon}
                        onClick={clearForm}
                        label={__("search.clear_input")}
                        dataTestId="searchInputClearButton"
                      />
                    ) : undefined
                  }
                  placeholder={translations?.placeholder ?? __("search.input_placeholder")}
                  aria-label={translations?.placeholder ?? __("search.input_placeholder")}
                  data-testid="searchInput"
                  ref={(node) => {
                    field.ref(node);
                    searchInputRef.current = node;
                  }}
                />
              )}
              validation={{
                minLength: minSearchTextLength,
              }}
            />
          </div>
          <div className={styles.buttonWrapper}>
            <SearchFormSubmitButton
              isPending={isPendingLocationChange}
              disabled={!isDirty || !isValid}
              label={translations?.buttonLabel ?? __("search.button_label")}
            />
          </div>
        </div>
      </Form>
    </div>
  );
};
